import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useClickOutside, useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'

import NavBarClasses from '../NavBar.module.scss'
import { SearchBar } from '../../SearchBar/SearchBar'

import countries from '@percent/workplace-giving/i18n/countries'
import { Icon, Modal } from '@percent/lemonade'
import { useAuth, useWindowDimensions } from '@percent/workplace-giving/common/hooks'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { getCountryCodeFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'

export function SpotlightSearch() {
  const { t } = useTranslation()
  const authState = useAuth()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const has = usePermission()
  const givingAiEnabled = has(scope('giving_ai'))
  const aiSearchEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_AI_SEARCH)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const { width: windowWidth } = useWindowDimensions()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('query') ?? undefined

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useClickOutside(() => setIsOpen(false))
  const [rawCountryCode, setRawCountryCode] = useState<string | undefined>(undefined)

  const handleClose = () => {
    setIsOpen(false)
  }

  const countryCode = useMemo(() => {
    if (rawCountryCode === '' || (rawCountryCode && countries.isValid(rawCountryCode))) {
      return rawCountryCode
    }

    const codeFromAuthState = getCountryCodeFromAuthState(authState.state)

    return codeFromAuthState && countries.isValid(codeFromAuthState) ? codeFromAuthState : ''
  }, [authState.state, rawCountryCode])

  const onCountryChange = (country: string) => {
    setRawCountryCode(country)
  }

  const onQueryChange = (searchedQuery: string) => {
    navigate({
      pathname: AppRoute.SEARCH,
      search: `?query=${encodeURIComponent(searchedQuery)}&country=${countryCode}`
    })

    track({
      event: 'Global Search',
      properties: {
        origin: 'Spotlight Search',
        countryCode
      }
    })

    handleClose()
  }

  useEffect(() => {
    document.body.setAttribute('style', `overflow-y:${isOpen ? 'hidden' : 'auto'}`)

    if (ref.current && isOpen && process.env.NX_PUBLIC_TEST_MODE !== 'true') {
      setTimeout(() => {
        ref.current?.querySelector('input')?.focus()
      }, 300)
    }
  }, [isOpen, ref])

  return (
    <>
      <button
        className={NavBarClasses.actionIconButtonSecondary}
        data-testid="spotlight-search-btn"
        type="button"
        onClick={() => {
          setIsOpen(true)
        }}
        aria-label={t('workplace_giving.common.search')}
      >
        <Icon name="search" color="primary" size={6} />
      </button>

      <Modal
        open={isOpen}
        enableFocus={false}
        onClose={handleClose}
        borderRadiiSize="xxlarge"
        width={isDesktop ? 775 : windowWidth - 48}
        position="top-center-lowered"
      >
        <div style={{ background: 'transparent' }} ref={ref}>
          <SearchBar
            query={query}
            onQueryChange={onQueryChange}
            countryCode={countryCode}
            onCountryChange={onCountryChange}
            size="medium"
            hideCountrySelector={aiSearchEnabled && givingAiEnabled}
            showAiButton={aiSearchEnabled && givingAiEnabled}
            onAiClick={aiQuery => {
              track({
                event: 'AI Search Clicked',
                properties: { origin: 'Spotlight', hasQuery: !!aiQuery }
              })
              handleClose()
              navigate(AppRoute.AI_SEARCH, { state: aiQuery ? { initialMessage: aiQuery } : undefined })
            }}
          />
        </div>
      </Modal>
    </>
  )
}
